import { PauseCircleOutlined, PlayCircleOutlined, SyncOutlined } from '@ant-design/icons'
import { Button, notification } from 'antd'
import dayjs from 'dayjs'
import { FC, useContext } from 'react'
import connect from '../../../../../helpers/api/connect'
import useErrorModal from '../../../../../hooks/api/use-error-modal'
import { userStorage } from '../../../../../services/user-storage'
import { useCreateRewindJobExecutorMutation } from '../../../../../store/api/rewind-job-executors-api'
import { useUpdateRewindJobMutation } from '../../../../../store/api/rewind-jobs-api'
import { rewindQueueEntriesApi } from '../../../../../store/api/rewind-queue-entries-api'
import { useAppDispatch } from '../../../../../store/store'
import RewindJobExecutor from '../../../../../types/api/rewind-job-executor'
import { RewindJobStatuses } from '../../../../../types/api/rewind-job'
import RewindQueueEntry from '../../../../../types/api/rewind-queue-entry'
import {
    isCurrentUserActiveExecutor,
    isRewindJobActive,
    isRewindJobInProgress,
    isRewindJobPaused,
} from '../helpers/rewind-job-helpers'
import { OneRewinderMachineScheduleContext } from '../one-rewinder-machine'

interface StartRewindProps {
    rewindQueueEntry: RewindQueueEntry
}

const StartRewind: FC<StartRewindProps> = ({ rewindQueueEntry }) => {
    const dispatch = useAppDispatch()
    const machineQueue = useContext(OneRewinderMachineScheduleContext)
    const rewindJob = rewindQueueEntry.rewind_job
    const currentUserDocumentId = userStorage.get()?.documentId

    const [updateRewindJob, updateRewindJobState] = useUpdateRewindJobMutation()
    const [createRewindJobExecutor, createRewindJobExecutorState] =
        useCreateRewindJobExecutorMutation()

    const isCurrentUserActive = isCurrentUserActiveExecutor(rewindJob, currentUserDocumentId)
    const isPaused = isRewindJobPaused(rewindJob)
    const isActive = isRewindJobActive(rewindJob)

    const machineHasUnfinishedRewind = machineQueue.some(entry =>
        isRewindJobInProgress(entry.rewind_job)
    )
    const isLoading = updateRewindJobState.isLoading || createRewindJobExecutorState.isLoading

    const connectExecutorToRewindJob = async (executor: RewindJobExecutor, isContinue: boolean) => {
        const result = await updateRewindJob({
            docId: rewindJob!.documentId,
            body: {
                data: {
                    ...(isContinue ? {} : { jobStatus: RewindJobStatuses.Rewinding }),
                    rewind_job_executors: connect(executor),
                },
            },
        })

        if (result.data) {
            dispatch(rewindQueueEntriesApi.util.invalidateTags(['RewindQueueEntries']))
            notification.success({
                message: isContinue ? 'Перемотка продолжена' : 'Перемотка начата',
            })
        }
    }

    const onCreateExecutor = async (isContinue: boolean) => {
        const result = await createRewindJobExecutor({
            data: {
                user: { connect: [currentUserDocumentId || ''] },
                machine: { connect: [rewindQueueEntry.machine?.documentId || ''] },
                start: dayjs().toISOString(),
            },
        })

        if (result.data) {
            await connectExecutorToRewindJob(result.data.data, isContinue)
        }
    }

    useErrorModal(createRewindJobExecutorState, 'Ошибка создания записи о перемотке')
    useErrorModal(updateRewindJobState, 'Ошибка обновления задания перемотки')

    if (isCurrentUserActive) {
        return (
            <Button
                style={{ width: '140px' }}
                icon={<SyncOutlined />}
                variant='solid'
                color='green'
            >
                Перематывается
            </Button>
        )
    }

    if (isPaused) {
        return (
            <Button
                icon={<PlayCircleOutlined />}
                type='primary'
                loading={isLoading}
                onClick={() => onCreateExecutor(true)}
            >
                Продолжить перемотку
            </Button>
        )
    }

    if (isActive) {
        return (
            <Button style={{ width: '140px' }} icon={<PauseCircleOutlined />} disabled>
                Занято
            </Button>
        )
    }

    return (
        <Button
            icon={<PlayCircleOutlined />}
            type='primary'
            disabled={machineHasUnfinishedRewind}
            loading={isLoading}
            onClick={() => onCreateExecutor(false)}
        >
            Начать перемотку
        </Button>
    )
}

export default StartRewind
